import type { ReactNode } from 'react'

type CornerIconGroupProps = {
    cornerIcons?: {
        topLeft?: ReactNode
        topRight?: ReactNode
        bottomLeft?: ReactNode
        bottomRight?: ReactNode
    }
    viewBoxWidth: number
    viewBoxHeight: number
    cornerIconSize: number
    cornerIconBackgroundColor: string
    cornerIconBackgroundShape: 'circle' | 'square' | 'none'
}

const camelToKebab = (str: string): string => str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()

export const CornerIconGroup = ({
    cornerIcons,
    viewBoxWidth,
    viewBoxHeight,
    cornerIconSize,
    cornerIconBackgroundColor,
    cornerIconBackgroundShape,
}: CornerIconGroupProps) => {
    if (!cornerIcons) return null

    const resolvedColor = cornerIconBackgroundColor.startsWith('#')
        ? cornerIconBackgroundColor
        : `var(--${camelToKebab(cornerIconBackgroundColor)})`

    const scale = cornerIconSize / Math.max(viewBoxWidth, viewBoxHeight)
    const positions = [
        { key: 'topLeft', x: 0, y: 0 },
        { key: 'topRight', x: viewBoxWidth - cornerIconSize, y: 0 },
        { key: 'bottomLeft', x: 0, y: viewBoxHeight - cornerIconSize },
        { key: 'bottomRight', x: viewBoxWidth - cornerIconSize, y: viewBoxHeight - cornerIconSize },
    ] as const

    return (
        <>
            {positions.map(({ key, x, y }) => {
                const icon = cornerIcons[key]

                if (!icon) return null

                const strokedIcon =
                    cornerIconBackgroundShape === 'none' ? (
                        icon
                    ) : (
                        <g style={{ stroke: resolvedColor }} strokeWidth={7} vectorEffect="non-scaling-stroke">
                            {icon}
                        </g>
                    )

                return (
                    <g key={key} transform={`translate(${x}, ${y}) scale(${scale})`}>
                        {strokedIcon}
                        {icon}
                    </g>
                )
            })}
        </>
    )
}

CornerIconGroup.displayName = 'CornerIconGroup'
